import { Box, Typography } from '@mui/material';
import { Save } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import type { AdminApi, AdminMaintenance, SensitiveActionInput } from '@/admin/adminApi';
import { SensitiveActionDialog } from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import { formatInstant } from '@/admin/users/accountPresentation';
import { Badge, Button, Card, Field, Switch } from '@/ui/kit';

import { adminErrorCode, describeAdminError } from './adminErrorMessage';
import { confirmationMessageFor, describeAction, describeServerState } from './maintenanceCopy';
import {
  MAINTENANCE_CONFIRMATION,
  MESSAGE_MAX_LENGTH,
  WINDOW_VIOLATIONS,
  changesOf,
  draftFromServer,
  isWindowViolation,
  localWindowViolation,
  sensitiveKindFor,
  timeZoneLabelFor,
  windowPhaseOf,
  type LastWrite,
  type MaintenanceDraft,
  type WindowViolation,
} from './maintenanceModel';
import { MaintenancePreview } from './MaintenancePreview';

/**
 * Formulaire de maintenance : commutateur, fenetre, message.
 *
 * Quatre regles, aucune cosmetique :
 *
 * 1. **L'etat affiche est celui du serveur, jamais celui du commutateur.** Le
 *    badge vient de `activeNow` (`GET /admin/maintenance`) : pendant une fenetre
 *    le commutateur est eteint et le service ne l'est pas. Apres une ecriture,
 *    l'ecran relit le serveur ; aucune valeur optimiste.
 * 2. **Une seule ecriture, une seule confirmation** : tout ce qui a change part
 *    ensemble sous un motif, un nom retape et — pour activer ou programmer — un
 *    mot de passe. Les refus de fenetre reviennent **sous les champs** concernes,
 *    la boite se referme : c'est la saisie qu'il faut corriger, pas la confirmation.
 * 3. **Un brouillon survit a la relecture.** Revenir sur l'onglet relit l'etat
 *    du serveur ; une saisie en cours n'est jamais ecrasee, sauf si elle etait
 *    encore identique a l'ancien etat.
 * 4. **Le role commande l'interface.** En `support` les champs sont en lecture
 *    seule et l'ecran le dit. Ce n'est pas la securite — le serveur tranche —,
 *    c'est de l'honnetete : un bouton qui mene a un 403 est un bouton qui ment.
 */

interface MaintenancePanelProps {
  readonly api: AdminApi;
  readonly state: AdminMaintenance;
  readonly lastWrite: LastWrite | null;
  readonly canWrite: boolean;
  /** Adresse de l'operateur, pour le gestionnaire de mots de passe de la boite. */
  readonly operatorEmail: string;
  /** Relit l'etat au serveur apres une ecriture reussie. Rend `false` si la relecture a echoue. */
  readonly onSaved: () => Promise<boolean>;
}

const sameDraft = (left: MaintenanceDraft, right: MaintenanceDraft): boolean =>
  left.enabled === right.enabled && left.start === right.start && left.end === right.end && left.message === right.message;

const describeLastWrite = (lastWrite: LastWrite | null): string =>
  lastWrite === null
    ? 'Jamais modifié : valeurs par défaut déclarées par le serveur.'
    : `Dernière modification le ${formatInstant(lastWrite.at)}${lastWrite.by === null ? ', sans auteur enregistré' : `, par le compte ${lastWrite.by}`}.`;

export const MaintenancePanel = ({
  api,
  state,
  lastWrite,
  canWrite,
  operatorEmail,
  onSaved,
}: MaintenancePanelProps): ReactElement => {
  const serverDraft = draftFromServer(state);
  const [base, setBase] = useState(serverDraft);
  const [draft, setDraft] = useState(serverDraft);

  // L'etat du serveur a change : le brouillon le suit s'il n'avait pas bouge,
  // sinon il est garde. Ajuste pendant le rendu plutot que dans un effet — un
  // effet peindrait d'abord l'ancienne valeur (meme patron que la boite).
  if (!sameDraft(base, serverDraft)) {
    setBase(serverDraft);

    if (sameDraft(draft, base)) {
      setDraft(serverDraft);
    }
  }

  const [pending, setPending] = useState<Readonly<Record<string, boolean | string>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [failureCode, setFailureCode] = useState<string | null>(null);
  const [violation, setViolation] = useState<WindowViolation | null>(null);
  const [saved, setSaved] = useState(false);
  const [rereadFailed, setRereadFailed] = useState(false);

  const timeZone = timeZoneLabelFor(draft.start);
  const changes = changesOf(draft, state);
  const dirty = Object.keys(changes).length > 0;
  const messageTooLong = draft.message.length > MESSAGE_MAX_LENGTH;
  const phase = windowPhaseOf(state);

  const edit = (patch: Partial<MaintenanceDraft>): void => {
    setDraft((current) => ({ ...current, ...patch }));
    setViolation(null);
    setSaved(false);
  };

  const requestSave = (): void => {
    if (!canWrite || !dirty || messageTooLong) {
      return;
    }

    // Ce qui se voit sans horloge est refuse ici, avant de demander un mot de
    // passe ; le reste (debut passe, duree) est juge par le serveur.
    const local = localWindowViolation(draft);

    if (local !== null) {
      setViolation(local);

      return;
    }

    setFailure(null);
    setFailureCode(null);
    setSaved(false);
    setPending(changes);
  };

  const commit = (input: SensitiveActionInput): void => {
    if (pending === null) {
      return;
    }

    setBusy(true);
    setFailure(null);
    setFailureCode(null);

    const run = async (): Promise<void> => {
      try {
        await api.updateSettings(pending, input);
      } catch (error: unknown) {
        const code = adminErrorCode(error);

        if (isWindowViolation(code)) {
          // La saisie est a corriger, pas la confirmation : la boite se ferme.
          setViolation(code);
          setPending(null);
        } else {
          setFailure(describeAdminError(error));
          setFailureCode(code);
        }

        setBusy(false);

        return;
      }

      // L'ecriture a reussi : l'etat affiche est relu au serveur, jamais deduit
      // de ce qui vient d'etre envoye.
      const reread = await onSaved();

      setRereadFailed(!reread);
      setSaved(reread);
      setPending(null);
      setBusy(false);
    };

    void run();
  };

  const kind = pending === null ? 'update_setting' : sensitiveKindFor(pending);
  const action = pending === null ? null : describeAction(pending, kind, timeZone);
  const policy = SENSITIVE_ACTIONS[kind];
  const violationFor = (field: 'start' | 'end'): string | undefined =>
    violation !== null && WINDOW_VIOLATIONS[violation].field === field ? WINDOW_VIOLATIONS[violation].message : undefined;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' }, gap: 2, alignItems: 'start' }}>
      <Card
        title="Mode maintenance"
        subtitle="Suspend l’API pour toute l’application mobile ; la connexion et l’administration restent servies."
        actions={
          state.activeNow ? (
            <Badge label="Maintenance en cours" tone="danger" dot />
          ) : (
            <Badge label="Service disponible" tone="success" dot />
          )
        }
      >
        <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>{describeServerState(state)}</Typography>

        {saved ? (
          <Box role="status" sx={{ fontSize: 14, color: 'tokens.successText' }}>
            {confirmationMessageFor(state)}
          </Box>
        ) : null}
        {rereadFailed ? (
          <Box role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
            L’écriture a réussi, mais l’état n’a pas pu être relu : actualisez l’écran avant de vous fier à ce qui est affiché.
          </Box>
        ) : null}
        {pending === null && failure !== null ? (
          <Box role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
            {failure}
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.75,
            borderRadius: '12px',
            bgcolor: 'tokens.surfaceMuted',
          }}
        >
          <Switch
            label="Activer immédiatement"
            hideLabel
            checked={draft.enabled}
            disabled={!canWrite}
            onChange={(enabled) => {
              edit({ enabled });
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="p" sx={{ fontSize: 14, fontWeight: 700 }} aria-hidden>
              Activer immédiatement
            </Typography>
            <Typography component="p" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Chaque requête reçoit 503 avec un délai de nouvel essai de cinq minutes.
            </Typography>
          </Box>
        </Box>

        <Box component="section" aria-labelledby="maintenance-window-title" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography id="maintenance-window-title" component="h3" sx={{ fontSize: 15, fontWeight: 700 }}>
              Planifier une fenêtre
            </Typography>
            {phase === 'ended' ? <Badge label="Fenêtre terminée" tone="neutral" /> : null}
            {phase === 'upcoming' ? <Badge label="Fenêtre à venir" tone="info" /> : null}
            {phase === 'running' ? <Badge label="Fenêtre en cours" tone="danger" /> : null}
          </Box>
          <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
            La bascule se fait seule, et le retour aussi. Heure saisie : <strong>{timeZone}</strong>, durée de 72 heures
            au plus.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Field
              label="Début"
              type="datetime-local"
              value={draft.start}
              disabled={!canWrite}
              error={violationFor('start')}
              onChange={(start) => {
                edit({ start });
              }}
            />
            <Field
              label="Fin"
              type="datetime-local"
              value={draft.end}
              disabled={!canWrite}
              error={violationFor('end')}
              onChange={(end) => {
                edit({ end });
              }}
            />
          </Box>
        </Box>

        <Field
          label="Message affiché dans l’application"
          multiline
          minRows={3}
          value={draft.message}
          disabled={!canWrite}
          error={messageTooLong ? `${String(MESSAGE_MAX_LENGTH)} caractères au plus : retirez-en ${String(draft.message.length - MESSAGE_MAX_LENGTH)}.` : undefined}
          hint={
            <>
              <strong>
                {String(draft.message.length)} / {String(MESSAGE_MAX_LENGTH)}
              </strong>{' '}
              caractères. Montré en bandeau, jamais en fenêtre bloquante.
            </>
          }
          onChange={(message) => {
            edit({ message });
          }}
        />

        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>{describeLastWrite(lastWrite)}</Typography>

        {canWrite ? (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              disabled={!dirty}
              onClick={() => {
                setDraft(serverDraft);
                setViolation(null);
              }}
            >
              Annuler
            </Button>
            <Button icon={Save} disabled={!dirty || messageTooLong} onClick={requestSave}>
              Enregistrer
            </Button>
          </Box>
        ) : null}
      </Card>

      <MaintenancePreview message={draft.message} />

      {action === null ? null : (
        <SensitiveActionDialog
          open
          title={action.title}
          subtitle="Maintenance du service"
          consequence={action.consequence}
          reasonOptions={policy.reasons}
          requiresPassword={policy.requiresPassword}
          operatorEmail={operatorEmail}
          // Une seule confirmation pour l'ensemble des reglages ecrits : le nom
          // de la famille, exactement.
          expectedConfirmation={MAINTENANCE_CONFIRMATION}
          confirmationLabel="le nom des réglages"
          actionLabel={action.actionLabel}
          pending={busy}
          error={failure}
          errorCode={failureCode}
          onCancel={() => {
            // L'echec reste affiche sous la boite refermee : fermer une boite
            // n'annule pas le refus qu'on vient d'y lire.
            setPending(null);
          }}
          onConfirm={commit}
        />
      )}
    </Box>
  );
};
